/**
 * QuoteViewPage.jsx — Consultation + validation publique d'un devis via lien signé HMAC.
 * Route publique (pas d'auth) : /enterprise/quote-view?token=<hmac>
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getApiUrl } from '../../config/environment';
import { toast } from 'sonner';
import {
  FileText, Download, CheckCircle2, XCircle, Upload, Loader2,
  ShieldCheck, Clock, Building2, Sparkles,
} from 'lucide-react';

const API = getApiUrl();

const QUOTE_STATUS_META = {
  draft: { label: 'Brouillon', color: '#6B7280' },
  sent: { label: 'Envoyé', color: '#1E40AF' },
  viewed: { label: 'Consulté', color: '#92400E' },
  accepted_online: { label: 'Signé en ligne', color: '#047857' },
  bdc_received: { label: 'BDC reçu', color: '#047857' },
  accepted: { label: 'Commande validée', color: '#065F46' },
  rejected: { label: 'Refusé', color: '#991B1B' },
  expired: { label: 'Expiré', color: '#6B7280' },
};

export default function QuoteViewPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // 'sign' | 'bdc' | null
  const [processing, setProcessing] = useState(false);

  // Sign state
  const [signForm, setSignForm] = useState({ signer_name: '', signer_email: '', signer_title: '' });
  // BDC state
  const [bdcForm, setBdcForm] = useState({ bdc_reference: '', signer_name: '', signer_email: '' });
  const [bdcFile, setBdcFile] = useState(null);

  useEffect(() => {
    if (!token) { setError('Lien invalide'); setLoading(false); return; }
    fetch(`${API}/enterprise/public/quote?token=${encodeURIComponent(token)}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setQuote)
      .catch(async (r) => {
        const e = await (typeof r.json === 'function' ? r.json().catch(() => ({})) : {});
        setError(e.detail || 'Lien invalide ou expiré');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const downloadPdf = () => window.open(`${API}/enterprise/public/quote/pdf?token=${encodeURIComponent(token)}`, '_blank');

  const handleSign = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const res = await fetch(`${API}/enterprise/public/quote/accept?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accept: true, ...signForm }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Erreur'); }
      toast.success('Signature enregistrée');
      const updated = await fetch(`${API}/enterprise/public/quote?token=${encodeURIComponent(token)}`).then(r => r.json());
      setQuote(updated); setMode(null);
    } catch (err) { toast.error(err.message); } finally { setProcessing(false); }
  };

  const handleBdc = async (e) => {
    e.preventDefault();
    if (!bdcFile) { toast.error('Veuillez joindre le BDC'); return; }
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append('bdc_reference', bdcForm.bdc_reference);
      fd.append('signer_name', bdcForm.signer_name);
      fd.append('signer_email', bdcForm.signer_email);
      fd.append('file', bdcFile);
      const res = await fetch(`${API}/enterprise/public/quote/bdc-upload?token=${encodeURIComponent(token)}`, {
        method: 'POST', body: fd,
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Erreur'); }
      toast.success('Bon de commande reçu');
      const updated = await fetch(`${API}/enterprise/public/quote?token=${encodeURIComponent(token)}`).then(r => r.json());
      setQuote(updated); setMode(null);
    } catch (err) { toast.error(err.message); } finally { setProcessing(false); }
  };

  if (loading) return <div className="p-10 text-center text-[#6B7280]">Chargement…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FBF9F3]">
      <div className="max-w-md bg-white border border-[#E8E5DE] rounded-2xl p-8 text-center" data-testid="quote-view-error">
        <XCircle className="w-12 h-12 text-[#991B1B] mx-auto mb-4" />
        <h1 className="text-xl font-display font-medium text-[#111827]">Lien invalide</h1>
        <p className="text-sm text-[#6B7280] mt-2">{error}</p>
      </div>
    </div>
  );
  if (!quote) return null;

  const meta = QUOTE_STATUS_META[quote.status] || {};
  const finalStates = ['accepted_online', 'bdc_received', 'accepted', 'rejected', 'expired'];
  const canAct = !finalStates.includes(quote.status) && quote.status !== 'expired';
  const snapshot = quote.company_snapshot || {};

  return (
    <div className="min-h-screen bg-[#FBF9F3] pb-20">
      <div className="bg-[#111827] text-white">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.3em] font-medium mb-3">Coach DIA · Offre Entreprise</div>
          <h1 className="text-3xl font-display font-medium">Votre devis {quote.quote_number}</h1>
          <p className="text-white/70 text-sm mt-1">Valable jusqu'au {new Date(quote.expires_at).toLocaleDateString('fr-FR')}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-4">
        {/* Status banner */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[#E8E5DE] rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3"
          data-testid="quote-view-status">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8] flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#92710A]" />
            </div>
            <div>
              <div className="text-sm text-[#111827] font-medium">{snapshot.company_name}</div>
              <div className="text-[11px] text-[#6B7280]">{snapshot.hr_contact_email}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</div>
            <div className="text-2xl font-semibold text-[#92710A]">
              {quote.total_ttc?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
            </div>
            <div className="text-[11px] text-[#6B7280]">TTC</div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-white border border-[#E8E5DE] rounded-2xl p-5">
          <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#92710A]" /> Détail de la proposition
          </h2>
          <div className="space-y-2 mb-4">
            {(quote.line_items || []).map((li, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[#F0EEE8] last:border-0 text-sm">
                <div>
                  <div className="text-[#111827]">{li.description}</div>
                  <div className="text-[11px] text-[#6B7280]">{li.quantity} × {li.unit_price_eur.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
                </div>
                <div className="text-[#111827] font-medium">{li.total_ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px] pt-3 border-t border-[#F0EEE8]">
            <div><span className="text-[#6B7280]">Validité</span><div className="text-[#111827]">{quote.validity_days} j</div></div>
            <div><span className="text-[#6B7280]">Paiement</span><div className="text-[#111827]">{quote.payment_terms_days} j fin de mois</div></div>
            <div><span className="text-[#6B7280]">Total HT</span><div className="text-[#111827]">{quote.subtotal_ht?.toLocaleString('fr-FR')} €</div></div>
            <div><span className="text-[#6B7280]">TVA {Math.round(quote.vat_rate * 100)}%</span><div className="text-[#111827]">incluse dans le TTC</div></div>
          </div>
          {quote.commercial_conditions && (
            <div className="mt-4 p-3 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8] text-[13px] text-[#4B5563] whitespace-pre-wrap">
              {quote.commercial_conditions}
            </div>
          )}
        </motion.div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          <button onClick={downloadPdf} data-testid="quote-view-pdf"
            className="px-4 py-2.5 rounded-full bg-white border border-[#E8E5DE] text-[#111827] text-sm font-medium hover:bg-[#F8F7F4] inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Télécharger le PDF
          </button>
          {canAct && (
            <>
              <button onClick={() => setMode('sign')} data-testid="quote-view-open-sign"
                className="px-4 py-2.5 rounded-full bg-[#059669] text-white text-sm font-medium hover:bg-[#047857] inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Signer en ligne
              </button>
              <button onClick={() => setMode('bdc')} data-testid="quote-view-open-bdc"
                className="px-4 py-2.5 rounded-full bg-[#111827] text-white text-sm font-medium hover:bg-[#1f2937] inline-flex items-center gap-2">
                <Upload className="w-4 h-4" /> Envoyer un BDC signé
              </button>
            </>
          )}
        </div>

        {/* Sign form */}
        {mode === 'sign' && canAct && (
          <form onSubmit={handleSign} className="bg-white border border-[#A7F3D0] rounded-2xl p-5 space-y-3" data-testid="quote-view-sign-form">
            <h3 className="text-sm font-display font-medium text-[#047857] flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Signature électronique simple
            </h3>
            <p className="text-[12px] text-[#6B7280]">
              Conformément au Règlement eIDAS 910/2014, votre signature fait foi dès lors que votre identité
              et votre accord sont consignés. Votre IP, le timestamp et l'empreinte du document sont enregistrés.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input required placeholder="Nom complet *" value={signForm.signer_name}
                onChange={(e) => setSignForm({ ...signForm, signer_name: e.target.value })}
                data-testid="sign-name" className="p-2 rounded-lg bg-[#F8F7F4] border border-[#E8E5DE] text-sm" />
              <input required type="email" placeholder="E-mail *" value={signForm.signer_email}
                onChange={(e) => setSignForm({ ...signForm, signer_email: e.target.value })}
                data-testid="sign-email" className="p-2 rounded-lg bg-[#F8F7F4] border border-[#E8E5DE] text-sm" />
              <input placeholder="Fonction (DRH, DAF…)" value={signForm.signer_title}
                onChange={(e) => setSignForm({ ...signForm, signer_title: e.target.value })}
                data-testid="sign-title" className="p-2 rounded-lg bg-[#F8F7F4] border border-[#E8E5DE] text-sm col-span-full" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setMode(null)}
                className="px-4 py-2 rounded-full border border-[#E8E5DE] bg-white text-[#4B5563] text-sm">Annuler</button>
              <button type="submit" disabled={processing}
                data-testid="sign-submit"
                className="px-5 py-2 rounded-full bg-[#059669] text-white text-sm font-medium hover:bg-[#047857] disabled:opacity-50 inline-flex items-center gap-2">
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Signer et valider le devis
              </button>
            </div>
          </form>
        )}

        {/* BDC form */}
        {mode === 'bdc' && canAct && (
          <form onSubmit={handleBdc} className="bg-white border border-[#E8DFC8] rounded-2xl p-5 space-y-3" data-testid="quote-view-bdc-form">
            <h3 className="text-sm font-display font-medium text-[#92710A] flex items-center gap-2">
              <Upload className="w-4 h-4" /> Envoi d'un bon de commande signé
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input required placeholder="N° de BDC *" value={bdcForm.bdc_reference}
                onChange={(e) => setBdcForm({ ...bdcForm, bdc_reference: e.target.value })}
                data-testid="bdc-reference" className="p-2 rounded-lg bg-[#F8F7F4] border border-[#E8E5DE] text-sm" />
              <input required placeholder="Votre nom *" value={bdcForm.signer_name}
                onChange={(e) => setBdcForm({ ...bdcForm, signer_name: e.target.value })}
                data-testid="bdc-name" className="p-2 rounded-lg bg-[#F8F7F4] border border-[#E8E5DE] text-sm" />
              <input required type="email" placeholder="E-mail *" value={bdcForm.signer_email}
                onChange={(e) => setBdcForm({ ...bdcForm, signer_email: e.target.value })}
                data-testid="bdc-email" className="p-2 rounded-lg bg-[#F8F7F4] border border-[#E8E5DE] text-sm" />
              <input required type="file" accept="application/pdf,image/*"
                onChange={(e) => setBdcFile(e.target.files?.[0])}
                data-testid="bdc-file" className="p-2 rounded-lg bg-[#F8F7F4] border border-[#E8E5DE] text-sm" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setMode(null)}
                className="px-4 py-2 rounded-full border border-[#E8E5DE] bg-white text-[#4B5563] text-sm">Annuler</button>
              <button type="submit" disabled={processing}
                data-testid="bdc-submit"
                className="px-5 py-2 rounded-full bg-[#111827] text-white text-sm font-medium hover:bg-[#1f2937] disabled:opacity-50 inline-flex items-center gap-2">
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Transmettre le BDC
              </button>
            </div>
          </form>
        )}

        {/* Final state message */}
        {finalStates.includes(quote.status) && (
          <div className="bg-white border border-[#A7F3D0] rounded-2xl p-5 text-center" data-testid="quote-view-final-state">
            {quote.status === 'accepted_online' && (
              <>
                <CheckCircle2 className="w-10 h-10 text-[#059669] mx-auto mb-2" />
                <div className="text-sm font-medium text-[#111827]">Devis signé en ligne</div>
                <p className="text-[11px] text-[#6B7280] mt-1">Nos équipes vont valider administrativement votre commande.</p>
              </>
            )}
            {quote.status === 'bdc_received' && (
              <>
                <CheckCircle2 className="w-10 h-10 text-[#047857] mx-auto mb-2" />
                <div className="text-sm font-medium text-[#111827]">Bon de commande reçu</div>
                <p className="text-[11px] text-[#6B7280] mt-1">Merci. Nous vous confirmerons la validation sous 48 h.</p>
              </>
            )}
            {quote.status === 'accepted' && (
              <>
                <CheckCircle2 className="w-10 h-10 text-[#065F46] mx-auto mb-2" />
                <div className="text-sm font-medium text-[#111827]">Commande validée</div>
                <p className="text-[11px] text-[#6B7280] mt-1">Votre programme va être lancé opérationnellement.</p>
              </>
            )}
            {quote.status === 'expired' && (
              <>
                <Clock className="w-10 h-10 text-[#6B7280] mx-auto mb-2" />
                <div className="text-sm font-medium text-[#111827]">Devis expiré</div>
                <p className="text-[11px] text-[#6B7280] mt-1">Contactez-nous pour obtenir une nouvelle proposition.</p>
              </>
            )}
            {quote.status === 'rejected' && (
              <>
                <XCircle className="w-10 h-10 text-[#991B1B] mx-auto mb-2" />
                <div className="text-sm font-medium text-[#111827]">Devis refusé</div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
